/**
 * Kid Money hub card. Prop-free and self-fetching so the integrator can drop it onto
 * either home screen.
 *
 *   Kids   — their total plus their nearest goal's progress.
 *   Adults — a snapshot of every child: total and nearest goal.
 *
 * Data comes from endpoints that derive the member from the verified identity (kids)
 * or the adults-only summary (adults), so a kid can only ever see their own numbers.
 * Links point at '/allowance' — the route the integrator wires KidMoneyPage onto.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError, money } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { KidGoal, KidWallet, MemberSummary } from '../../shared/types';
import {
  Avatar,
  GoalBar,
  bucketMeta,
  cx,
  nearestGoal,
  walletTotal,
  type KidSummary,
} from './shared';

const ROUTE = '/allowance';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-border bg-surface p-5 sm:p-6">{children}</section>
  );
}

function Title() {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink">
        <span aria-hidden="true">🐷</span> Kid Money
      </h2>
      <Link
        to={ROUTE}
        className="text-sm font-medium text-sage hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        Open
      </Link>
    </div>
  );
}

/** One goal's line: emoji, name, a thin bar, and how close it is. */
function GoalLine({ goal }: { goal: KidGoal }) {
  const meta = bucketMeta(goal.bucket);
  const done = !!goal.achieved_at;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex min-w-0 items-center gap-1.5">
          <span aria-hidden="true">{goal.emoji ?? '🎯'}</span>
          <span className="truncate text-ink">{goal.name}</span>
        </span>
        <span className={cx('shrink-0 font-medium', done ? 'text-sage' : 'text-ink-soft')}>
          {done ? 'Reached 🎉' : `${money(goal.saved)} / ${money(goal.target)}`}
        </span>
      </div>
      <div className="mt-1.5">
        <GoalBar percent={goal.percent} accent={done ? 'var(--color-sage)' : meta.accent} />
      </div>
    </div>
  );
}

export function KidMoneyCard() {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [summary, setSummary] = useState<KidSummary[] | null>(null);
  const [wallet, setWallet] = useState<KidWallet | null>(null);
  const [goals, setGoals] = useState<KidGoal[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const me = useMemo<MemberSummary | null>(
    () =>
      identity.memberId == null
        ? null
        : {
            id: identity.memberId,
            name: identity.name ?? 'Me',
            color: identity.color ?? 'var(--color-sage)',
            role: identity.role,
          },
    [identity.memberId, identity.name, identity.color, identity.role]
  );

  const load = useCallback(async () => {
    setError(false);
    try {
      if (isAdult) {
        setSummary(await api.get<KidSummary[]>('/kidmoney/summary'));
      } else if (identity.memberId == null) {
        setWallet({ member_id: 0, spend: 0, save: 0, give: 0, pending: 0 });
        setGoals([]);
      } else {
        const [w, g] = await Promise.all([
          api.get<KidWallet>('/kidmoney/wallet'),
          api.get<KidGoal[]>('/kidmoney/goals'),
        ]);
        setWallet(w);
        setGoals(g);
      }
    } catch (err) {
      // A failed read must never render as an all-clear or a fabricated zero.
      if (!(err instanceof ApiRequestError)) console.error('KidMoneyCard load failed', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isAdult, identity.memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Shell>
        <Title />
        <div className="mt-4 space-y-2" role="status" aria-label="Loading">
          <div className="h-8 w-32 animate-pulse rounded-lg bg-surface-sunk" />
          <div className="h-3 w-full animate-pulse rounded-full bg-surface-sunk" />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <Title />
        <div className="mt-4">
          <p className="text-sm text-ink-soft">We couldn’t load this right now.</p>
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              void load();
            }}
            className="mt-2 text-sm font-medium text-sage hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  // ---- adult: every kid ----
  if (isAdult) {
    const kids = summary ?? [];
    return (
      <Shell>
        <Title />
        {kids.length === 0 ? (
          <p className="mt-4 text-sm text-ink-soft">
            No kids set up yet. Add a child in Settings → Family.
          </p>
        ) : (
          <ul className="mt-4 space-y-4">
            {kids.map((kid) => {
              const near = nearestGoal(kid.goals);
              return (
                <li key={kid.member.id} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Avatar member={kid.member} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                      {kid.member.name}
                    </span>
                    <span
                      className="font-display text-lg font-semibold tabular-nums"
                      style={{ color: kid.member.color }}
                    >
                      {money(walletTotal(kid.wallet))}
                    </span>
                  </div>
                  {near ? <GoalLine goal={near} /> : (
                    <p className="text-xs text-ink-faint">No goal set yet.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Shell>
    );
  }

  // ---- kid: their own money ----
  const w = wallet!;
  const near = nearestGoal(goals ?? []);
  const total = walletTotal(w);

  return (
    <Shell>
      <Title />
      <div className="mt-4">
        <p
          className="font-display text-4xl leading-none font-semibold tabular-nums"
          style={{ color: me?.color ?? 'var(--color-ink)' }}
        >
          {money(total)}
        </p>
        <p className="mt-2 text-sm text-ink-soft">
          {total === 0 ? 'Finish a chore and your money starts here.' : 'is yours, split across your buckets.'}
        </p>

        {w.pending > 0 ? (
          <p className="mt-3 rounded-xl bg-honey-soft px-3 py-2 text-sm text-ink">
            <span className="font-semibold">{money(w.pending)}</span> waiting on a grown-up.
          </p>
        ) : null}

        {near ? (
          <div className="mt-4">
            <GoalLine goal={near} />
          </div>
        ) : total > 0 ? (
          <Link
            to={ROUTE}
            className="mt-4 inline-flex min-h-11 items-center text-sm font-medium text-sage hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Set a savings goal →
          </Link>
        ) : null}
      </div>
    </Shell>
  );
}

export default KidMoneyCard;
